<script setup lang="ts">
import { computed } from 'vue'
import type {
  ExternalCourse,
  SubjectRequest,
} from '@/models/current-student-advanced-standing-form/api'
import {
  articulationAutoAddedCourseSubjectRequests,
  externalCourseAllowsLtuSubjectRequests,
  externalCourseRequiresArticulationForCredit,
} from '@/models/current-student-advanced-standing-form/previousLearning'
import type { CourseRuleOutcomeRow } from '@/models/current-student-advanced-standing-form/precedents'

const props = defineProps<{
  course: ExternalCourse
  courseRuleOutcomeRows: (course: ExternalCourse) => CourseRuleOutcomeRow[]
  outcomePriorityClass: (outcome: CourseRuleOutcomeRow) => string
}>()

const rows = computed(() => props.courseRuleOutcomeRows(props.course))
const articulationAutoAddedRequests = computed(() => {
  const outcomeSubjectCodes = new Set(rows.value.map((row) => row.subject_code))
  return articulationAutoAddedCourseSubjectRequests(props.course).filter((request) => {
    return !outcomeSubjectCodes.has(request.subject_code)
  })
})
const hasContent = computed(() => {
  return articulationAutoAddedRequests.value.length > 0 || rows.value.length > 0
})
const showArticulationOnlyCredit = computed(() => {
  return externalCourseRequiresArticulationForCredit(props.course)
    && props.course.articulation_agreements.length > 0
    && hasContent.value
})

function outcomeDisplaySubjectName(outcome: CourseRuleOutcomeRow, index: number): string {
  if (articulationAutoAddedRequests.value.length === 0 || index + 1 < rows.value.length) {
    return outcome.displaySubjectName
  }

  return `${outcome.displaySubjectName}, or`
}

function articulationAutoAddedRequestDisplaySubjectName(request: SubjectRequest, index: number): string {
  const subjectName = request.subject_name ?? ''
  return index + 1 < articulationAutoAddedRequests.value.length ? `${subjectName}, or` : subjectName
}
</script>

<template>
  <table v-if="externalCourseAllowsLtuSubjectRequests(course) || showArticulationOnlyCredit"
    class="table PrecedentsTable PrecedentsTableCourses">
    <thead>
      <tr>
        <th>La Trobe Subject Code</th>
        <th>La Trobe Subject Name</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <tr v-if="!hasContent">
        <td colspan="3" class="text-center">== No La Trobe subject requested ==</td>
      </tr>
      <tr v-for="(outcome, index) in rows" :key="`${course.external_course_id}-${outcome.subject_code}`">
        <td class="OutcomeBox" :class="outcomePriorityClass(outcome)" :title="outcome.title">
          {{ outcome.subject_code }}
        </td>
        <td colspan="2">{{ outcomeDisplaySubjectName(outcome, index) }}</td>
      </tr>
      <tr v-for="(subjectRequest, index) in articulationAutoAddedRequests"
        :key="`${course.external_course_id}-articulation-${subjectRequest.subject_code}-${index}`">
        <td>{{ subjectRequest.subject_code }}</td>
        <td colspan="2">{{ articulationAutoAddedRequestDisplaySubjectName(subjectRequest, index) }}</td>
      </tr>
    </tbody>
  </table>
</template>
